import React, { useEffect, useRef, useState } from 'react';
import { Grid } from '@mui/material';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { rescheduleTheme } from '../rescheduleTheme';
import { RescheduleTypography } from './RescheduleTypography';

type CountdownProps = {
  expiresAt: string;
  onExpire: () => void;
};

const TWO_MINUTES_IN_SECONDS = 120;

const getRemainingSeconds = (expiresAt: string) =>
  Math.max(0, Math.ceil(moment.utc(expiresAt).diff(moment.utc(), 'seconds', true)));

const formatClock = (totalSeconds: number) =>
  `${Math.floor(totalSeconds / 60)}:${String(totalSeconds % 60).padStart(2, '0')}`;

export const Countdown = ({ expiresAt, onExpire }: CountdownProps) => {
  const { t } = useTranslation();
  const [totalSeconds] = useState(() => getRemainingSeconds(expiresAt));
  const [remainingSeconds, setRemainingSeconds] = useState(totalSeconds);
  const hasExpiredRef = useRef(false);
  const [announcement, setAnnouncement] = useState('');
  const hasAnnouncedTwoMinutesRef = useRef(false);

  useEffect(() => {
    const interval = setInterval(() => setRemainingSeconds(getRemainingSeconds(expiresAt)), 1000);
    return () => clearInterval(interval);
  }, [expiresAt]);

  useEffect(() => {
    if (remainingSeconds === 0 && !hasExpiredRef.current) {
      hasExpiredRef.current = true;
      setAnnouncement(t('manage_booking.v2.timeRanOut'));
      onExpire();
      return;
    }
    if (remainingSeconds > 0 && remainingSeconds <= TWO_MINUTES_IN_SECONDS && !hasAnnouncedTwoMinutesRef.current) {
      hasAnnouncedTwoMinutesRef.current = true;
      setAnnouncement(t('manage_booking.v2.twoMinutesLeft'));
    }
  }, [remainingSeconds, onExpire, t]);

  return (
    <Grid container flexDirection="column" flexWrap="nowrap" gap="8px">
      <Grid container flexWrap="nowrap" justifyContent="space-between" alignItems="flex-end">
        <RescheduleTypography style={{ fontSize: 13, color: rescheduleTheme.textMuted }}>
          {t('manage_booking.v2.timeLeft')}
        </RescheduleTypography>
        <RescheduleTypography
          role="timer"
          aria-label={t('manage_booking.v2.timeLeft')}
          dir="ltr"
          style={{
            fontSize: 28,
            fontWeight: 600,
            fontVariantNumeric: 'tabular-nums',
            color: remainingSeconds === 0 ? rescheduleTheme.textMuted : rescheduleTheme.brand,
          }}>
          {formatClock(remainingSeconds)}
        </RescheduleTypography>
      </Grid>
      <Grid
        item
        aria-hidden="true"
        sx={{ height: '6px', borderRadius: '3px', background: rescheduleTheme.brandTint, overflow: 'hidden' }}>
        <Grid
          item
          sx={{
            width: `${totalSeconds > 0 ? (remainingSeconds / totalSeconds) * 100 : 0}%`,
            height: '100%',
            background: rescheduleTheme.brand,
          }}
        />
      </Grid>
      <RescheduleTypography
        component="span"
        aria-live="polite"
        style={{
          position: 'absolute',
          width: 1,
          height: 1,
          overflow: 'hidden',
          clip: 'rect(0 0 0 0)',
          whiteSpace: 'nowrap',
        }}>
        {announcement}
      </RescheduleTypography>
    </Grid>
  );
};
